import { RegisteredPatient } from "@domain/patient/patient";
import { PatientDatedProgramSummary, PatientProgram } from "@domain/program/patient-program";
import {
	ExtendableProgramStatusProps,
	GlobalProgramStatus,
	GlobalProgramStatusType,
	ProgramStatus,
} from "@domain/program/program-status/program-status";
import { isNotNil, isNil } from "@utils/assert";
import { computeDiffInDays, now, TWO_MONTH_IN_DAYS } from "@utils/date-utils";

export const extractGlobalProgramStatusFromPatient = ({
	previousProgram,
	ongoingProgram,
	nextProgram,
}: {
	previousProgram?: PatientDatedProgramSummary;
	ongoingProgram?: PatientDatedProgramSummary;
	nextProgram?: PatientDatedProgramSummary;
}): GlobalProgramStatus => {
	if (isNotNil(ongoingProgram)) {
		return {
			type:
				isNil(nextProgram) && computeDiffInDays(now(), ongoingProgram.endDate) < TWO_MONTH_IN_DAYS
					? GlobalProgramStatusType.EndingSoonProgramWithNoNext
					: GlobalProgramStatusType.OngoingProgram,
			startDate: ongoingProgram.startDate,
			endDate: ongoingProgram.endDate,
			programId: ongoingProgram.id,
		};
	}
	if (isNotNil(nextProgram)) {
		return { type: GlobalProgramStatusType.ScheduledProgram, startDate: nextProgram.startDate };
	}
	if (isNotNil(previousProgram)) {
		return {
			type: GlobalProgramStatusType.EndedLastProgram,
			endDate: previousProgram.endDate,
			programId: previousProgram.id,
		};
	}
	return { type: GlobalProgramStatusType.NoProgram };
};

export const extractPropsForProgramStatusComponentFromPatientProgram = (
	program: PatientProgram,
	inDetail?: boolean,
): GlobalProgramStatus => {
	switch (program.status) {
		case ProgramStatus.Ongoing:
			return {
				type: GlobalProgramStatusType.OngoingProgram,
				startDate: program.startDate,
				endDate: program.endDate,
				programId: program.id,
			};
		case ProgramStatus.Scheduled:
			return { type: GlobalProgramStatusType.ScheduledProgram, startDate: program.startDate };
		case ProgramStatus.Ended:
			return {
				type: inDetail ? GlobalProgramStatusType.EndedLastProgramDetailsMode : GlobalProgramStatusType.EndedLastProgram,
				endDate: program.endDate,
				programId: program.id,
			};
		default:
			return { type: GlobalProgramStatusType.NoProgram };
	}
};

const ExtendableProgramsStatus = [
	GlobalProgramStatusType.EndingSoonProgramWithNoNext,
	GlobalProgramStatusType.EndedLastProgram,
	GlobalProgramStatusType.EndedLastProgramDetailsMode,
];

export const isGlobalProgramStatusExtendable = (props: GlobalProgramStatus): props is ExtendableProgramStatusProps =>
	ExtendableProgramsStatus.includes(props.type);

export const hasPatientExtendableProgram = (patient: RegisteredPatient): boolean =>
	isGlobalProgramStatusExtendable(patient.globalProgramStatus);

const extractUnixDateFromGlobalProgramStatus = (status: GlobalProgramStatus): number => {
	switch (status.type) {
		case GlobalProgramStatusType.ScheduledProgram:
			return status.startDate.unix();
		case GlobalProgramStatusType.EndedLastProgram:
			return status.endDate.unix();
		case GlobalProgramStatusType.OngoingProgram:
			return status.endDate.unix();
		case GlobalProgramStatusType.EndedLastProgramDetailsMode:
			return status.endDate.unix();
		case GlobalProgramStatusType.EndingSoonProgramWithNoNext:
			return status.endDate.unix();
		case GlobalProgramStatusType.NoProgram:
			return 0;
	}
};

const SortedGlobalStatus = [
	GlobalProgramStatusType.NoProgram,
	GlobalProgramStatusType.EndedLastProgram,
	GlobalProgramStatusType.EndedLastProgramDetailsMode,
	GlobalProgramStatusType.EndingSoonProgramWithNoNext,
	GlobalProgramStatusType.OngoingProgram,
	GlobalProgramStatusType.ScheduledProgram,
];
export const sortGlobalProgramStatus = (a: GlobalProgramStatus, b: GlobalProgramStatus, order: 0 | 1 | -1): number => {
	if (a.type === b.type) {
		return order * (extractUnixDateFromGlobalProgramStatus(a) - extractUnixDateFromGlobalProgramStatus(b));
	} else {
		return SortedGlobalStatus.indexOf(a.type) > SortedGlobalStatus.indexOf(b.type) ? order : -order;
	}
};
